import React, { useState, useEffect } from 'react';
import Dialog from 'src/components/common/Dialog.redesign';
import { AnyObject } from 'final-form';
import Form from 'src/components/controls/forms/Form';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import Textbox from 'src/components/controls/forms/redesign/FormTextbox';
import { SelectItem } from 'src/models/common/SelectItem';
import TagContainer from 'src/components/counties/TagContainer';
import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import { Url } from 'src/api';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import County from 'src/models/County';
import Tag from 'src/models/Tag';
import Township from 'src/models/Township';
import { FormErrors } from 'src/util/FormErrors';
import PropertyUtil from 'src/util/PropertyUtil';
import Meridian from 'src/models/Meridian';
import { FormApi } from 'final-form';
import { LandSystem } from 'src/models/LandSystem';

interface ITownshipDialogProps {
    onCloseDialog?: () => void;
    action: 'create' | 'edit' | 'delete';
    townshipId: number;
    onUpdateComplete: (townshipId: number) => void;
    landSystem: LandSystem;
}

const TownshipDialog: React.FC<ITownshipDialogProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const [formApi, setFormApi] = useState<FormApi>(null);
    const { get, post, put, response } = useFetch();
    const [currentTownship, setCurrentTownship] = useState(undefined);
    const [meridians, setMeridians] = useState([]);
    const [counties, setCounties] = useState([]);
    const [tags, setTags] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);

    const loadData = async () => {
        if (props.action === 'edit') {
            const data = await get(`${Url.project.getTownship(appContext, props.townshipId)}`);

            if (response.ok) {
                setCurrentTownship(data);
            }
        }

        const meridiansCall = get(Url.project.getProjectMeridians(appContext));
        const counitesCall = get(Url.project.getProjectCounties(appContext));
        const tagsCall = get(Url.project.getProjectTags(appContext));

        const [meridiansData, countiesData, tagsData] = [await meridiansCall, await counitesCall, await tagsCall];

        if (response.ok) {
            setMeridians(meridiansData);
            setCounties(countiesData.items);
            setTags(tagsData);
            setDataLoaded(true);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const twnCard = [new SelectItem('N', 'N'), new SelectItem('S', 'S')];

    const rngCard = [new SelectItem('E', 'E'), new SelectItem('W', 'W')];

    const parseTownshipModel = (addTownshipModel: Township) => {
        const township = new Township();
        if (addTownshipModel.meridian) {
            township.meridian = new Meridian();
            township.meridian.id = addTownshipModel.meridian.id;
        }

        const townshipRegex = /^([1-9]\d{0,4})$/;
        const rangeRegex = /^([1-9]\d{0,4})$/;

        if (addTownshipModel.townshipCardinal || addTownshipModel.townshipNumber) {
            const townshipMatches = townshipRegex.exec(addTownshipModel?.townshipNumber?.toString() || undefined);
            if (townshipMatches) {
                township.townshipNumber = parseInt(townshipMatches[1], 10);
                township.townshipCardinal = twnCard.find((i) => i.id === addTownshipModel.townshipCardinal).name;
            }
        }

        if (addTownshipModel.rangeCardinal || addTownshipModel.rangeNumber) {
            const rangeMatches = rangeRegex.exec(addTownshipModel?.rangeNumber?.toString());
            // TODO: this is problematic, range can be saved as a null 'char' in the db, or gets return from API. Need cleaner way to handle
            if (rangeMatches) {
                township.rangeNumber = parseInt(rangeMatches[1], 10);
                township.rangeCardinal = rngCard.find((i) => i.id === addTownshipModel.rangeCardinal).name;
            }
        }

        township.block = addTownshipModel.block;

        township.addCounties = addTownshipModel.addCounties;
        township.deleteCounties = addTownshipModel.deleteCounties;
        township.addTags = addTownshipModel.addTags;
        township.deleteTags = addTownshipModel.deleteTags;

        return township;
    };

    const validateTownshipForm = (addTownshipModel: Township) => {
        const errors: FormErrors<Township & { generalError?: string }> = {
            townshipNumber: undefined,
            townshipCardinal: undefined,
            rangeNumber: undefined,
            rangeCardinal: undefined,
        };

        const townshipNumberRegex = /^[1-9]\d{0,4}$/;
        const rangeNumberRegex = /^[1-9]\d{0,4}$/;

        const townshipNumber = PropertyUtil.SafeGet(() => addTownshipModel.townshipNumber.toString().trim());
        const townshipCardinal = PropertyUtil.SafeGet(() => addTownshipModel.townshipCardinal);
        const rangeNumber = PropertyUtil.SafeGet(() => addTownshipModel.rangeNumber.toString().trim());
        const rangeCardinal = PropertyUtil.SafeGet(() => addTownshipModel.rangeCardinal);

        if (props.landSystem === LandSystem.Jeffersonian) {
            if (!townshipNumber || townshipNumber === '') {
                errors.townshipNumber = 'Required';
            } else if (!townshipNumberRegex.test(addTownshipModel.townshipNumber.toString())) {
                errors.townshipNumber = 'Please enter a valid township number';
            }
            if (townshipCardinal === undefined) {
                errors.townshipCardinal = 'Required';
            }

            if (!rangeNumber || rangeNumber === '') {
                errors.rangeNumber = 'Required';
            } else if (!rangeNumberRegex.test(addTownshipModel.rangeNumber.toString())) {
                errors.rangeNumber = 'Please enter a valid range number';
            }
            if (rangeCardinal === undefined) {
                errors.rangeCardinal = 'Required';
            }
        } else {
            if (!addTownshipModel.block) {
                errors.block = 'Block is required.';
            }

            if (townshipNumber || townshipCardinal) {
                if (!townshipNumber) {
                    errors.townshipNumber = 'Required or Remove Township Cardinal';
                }

                if (!townshipCardinal) {
                    errors.townshipCardinal = 'Required or Remove Township Number';
                }
            }
        }

        return errors as FormErrors<AnyObject>;
    };

    const submitForm = async (values: AnyObject) => {
        if (props.action === 'create') {
            const result = await post(Url.project.getTownships(appContext), parseTownshipModel(values as Township));

            if (response.ok) {
                props.onUpdateComplete(result.id);
                props.onCloseDialog();
            }
        }

        if (props.action === 'edit') {
            const result = await put(Url.project.getTownship(appContext, props.townshipId), parseTownshipModel(values as Township));

            if (response.ok) {
                props.onUpdateComplete(result.id);
                props.onCloseDialog();
            }
        }
    };

    const dialogTitle = () => {
        switch (props.action) {
            case 'create':
                return 'Create Township';
                break;
            case 'edit':
                return 'Edit Township';
                break;
            case 'delete':
                return 'Delete Township';
                break;
            default:
                return '[Error]';
                break;
        }
    };

    const dialogActionButton = () => {
        switch (props.action) {
            case 'create':
                return 'Create Township';
                break;
            case 'edit':
                return 'Save Changes';
                break;
            case 'delete':
                return 'Delete Township';
                break;
            default:
                return '[Error]';
                break;
        }
    };

    const landSystemRangeMeridian = () => {
        if (props.landSystem === LandSystem.WestTexas) {
            return <></>;
        }

        return (
            <>
                <Textbox name="rangeNumber" label="Range Number" />
                <FormSelect name="rangeCardinal" label="Range Cardinal" items={rngCard} />
                <FormSelect
                    name="meridian.id"
                    label="Meridian"
                    defaultOption="Select Meridian"
                    items={
                        meridians && meridians.length > 0
                            ? meridians.map((meridian) => new SelectItem(meridian.id, meridian.name))
                            : new Array<SelectItem>()
                    }
                />
            </>
        );
    };

    const form = (initialValues: AnyObject) => {
        console.log('init vals', initialValues);
        return (
            <Form
                onSubmit={submitForm}
                validate={validateTownshipForm}
                initialValues={initialValues}
                onFormCreated={!formApi && setFormApi}
                hasFieldArray={true}
                showDebugValues={true}
            >
                <div>
                    {formApi && formApi.getState().submitFailed && (
                        <span>{`${formApi?.getState()?.error} - ${formApi?.getState()?.hasValidationErrors}`}</span>
                    )}
                </div>
                {props.landSystem === LandSystem.WestTexas ? <Textbox name="block" label="Block" /> : <></>}
                <Textbox name="townshipNumber" label="Township Number" />
                <FormSelect name="townshipCardinal" label="Township Cardinal" items={twnCard} />
                {landSystemRangeMeridian()}
                <TagContainer
                    label="Counties"
                    fieldArrayName="counties"
                    fieldAddArrayName="addCounties"
                    fieldDeleteArrayName="deleteCounties"
                    initialValueName="county"
                    isTagCreatable={false}
                    form={formApi}
                    selectTags={counties.length > 0 ? counties.map((c: County) => new SelectItem(c.id, c.name)) : new Array<SelectItem>()}
                />
                <TagContainer
                    label="Tags"
                    fieldArrayName="tags"
                    fieldAddArrayName="addTags"
                    fieldDeleteArrayName="deleteTags"
                    initialValueName="tag"
                    isTagCreatable={true}
                    form={formApi}
                    selectTags={tags.length > 0 ? tags.map((t: Tag) => new SelectItem(t.id, t.name)) : new Array<SelectItem>()}
                />
                <Button type="submit" text={dialogActionButton()} />
            </Form>
        );
    };

    return (
        <Dialog title={dialogTitle()}>
            {dataLoaded &&
                props.action === 'create' &&
                form({ meridian: { id: meridians?.filter((m: Meridian) => m.isPrimary)[0]?.id || undefined } })}
            {/* {dataLoaded && props.action === 'edit' && form({ ...currentTownship, townshipNumber: currentTownship.townshipNumber === '0' ? '' : currentTownship.townshipNumber })} */}
            {dataLoaded && props.action === 'edit' && form({ ...currentTownship })}
        </Dialog>
    );
};

export default TownshipDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/TownshipDialog.tsx